import {
  RouteProp,
  StackActions,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../components/AlertBox';
import { useGetStudyIntake } from '../../../hooks/study/useGetStudyIntake';
import { DrawerParamList } from '../../../navigation/DrawerNavigator';
import { FormElementProps } from '../../FormElement';
import { useGetFormTemplate } from '../../hooks/useGetFormTemplate';
import { FormElementPlugin } from '../FormElementPlugin';
import { useDynamicFormContext } from '../../DynamicFormContext';

const StyledViewContainer = styled.View``;

type RedirectProps<T> = FormElementProps<T, RedirectConfig>;

export const Redirect = <T extends object>({
  element,
  stateNode
}: RedirectProps<T>) => {
  const { emitEvent } = useDynamicFormContext<any, any>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route = useRoute<RouteProp<DrawerParamList, 'FormTemplate'>>();
  const studyParticipantId = route.params?.studyParticipantId;
  const { redirectPath, redirectFormTemplateId, shouldLoadExistingFormData } =
    element.config || {};

  const { formTemplate, loadingFormTemplateError, getFormTemplate } =
    useGetFormTemplate(redirectFormTemplateId || 0);
  const [entityDataId, setEntityDataId] = useState<number>();
  const { studyIntake, loadingStudyIntakeError, getStudyIntake } =
    useGetStudyIntake();
  const [error, setError] = useState('');
  useEffect(() => {
    if (shouldLoadExistingFormData && formTemplate?.entity?.rules?.length) {
      const rule = formTemplate?.entity?.rules[0];
      if (rule.type === 'study-intake' && studyParticipantId) {
        // if intake form or study form then use studyParticipantId
        getStudyIntake(studyParticipantId);
      } else {
        // else error
        setError(
          'No existing data found. Currently only intake forms are supported.'
        );
      }
    }
  }, [
    formTemplate?.entity?.rules,
    getStudyIntake,
    shouldLoadExistingFormData,
    studyParticipantId
  ]);
  useEffect(() => {
    if (studyIntake) setEntityDataId(studyIntake.id);
  }, [studyIntake]);

  // console.log('shouldLoadExistingFormData', shouldLoadExistingFormData);
  // console.log('redirect formTemplate', formTemplate);
  // console.log('studyIntake', studyIntake);

  // // redirect after assigning study template
  // console.log(
  //   'redirectPath, redirectFormTemplateId',
  //   redirectPath,
  //   redirectFormTemplateId
  // );
  useEffect(() => {
    // Capture stateNode.type at effect-fire time. handleRedirect is deferred
    // to the next render loop via setTimeout (see comment below), so by the
    // time it runs the state machine may have transitioned away from 'final'
    // — a post-await check would return false and 'final' would silently
    // never emit, leaving the form stuck after a successful redirect.
    const wasFinal = stateNode?.type === 'final';
    const handleRedirect = async () => {
      if (wasFinal) {
        await emitEvent('final', { transitions: [] });
      }
      if (
        !entityDataId &&
        redirectPath === 'FormTemplate' &&
        shouldLoadExistingFormData
      ) {
        // get form template and check entity rules
        getFormTemplate();
        return;
      }
      if (redirectPath !== 'PreviousScreen') {
        if (redirectPath === 'FormTemplate') {
          // console.log('push form template', redirectFormTemplateId);
          navigation.push('FormTemplate', {
            formTemplateId: redirectFormTemplateId,
            studyParticipantId: studyParticipantId,
            entityDataId
          });
          return;
        } else if (redirectPath === 'MyData') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'MyDataNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudiesNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudyComingSoon') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav',
                params: {
                  screen: 'StudyComingSoon'
                }
              }
            })
          );
          return;
        } else if (redirectPath === 'ToDoNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'ToDoNav'
              }
            })
          );
          return;
        }
      }
      // default to the previous screen prior to the FormTemplate
      const navigationState = navigation.getState();
      let popCount = 0;
      // console.log('navigationState.routes', navigationState.routes);
      for (let i = navigationState.routes.length - 1; i >= 0; i--) {
        if (
          navigationState.routes[i].name !== 'StudyEventForm' &&
          navigationState.routes[i].name !== 'FormTemplate'
        ) {
          navigation.pop(popCount);
          return;
        }
        popCount++;
      }
      // else navigate to My Chloe
      navigation.dispatch(
        StackActions.replace('DrawerNavigator', {
          screen: 'BottomTabNav',
          params: {
            screen: 'HomeNav'
          }
        })
      );
    };
    // wait till next render loop since StudyEventFormDataSource doesn't
    // register the 'final' subscribe immediately
    setTimeout(() => handleRedirect());
  }, [
    stateNode?.type,
    navigation,
    redirectPath,
    entityDataId,
    redirectFormTemplateId,
    shouldLoadExistingFormData,
    getFormTemplate,
    emitEvent,
    studyParticipantId
  ]);

  return error ? (
    <AlertBox severity='error'>{error}</AlertBox>
  ) : loadingFormTemplateError ? (
    <AlertBox severity='error'>{loadingFormTemplateError.message}</AlertBox>
  ) : loadingStudyIntakeError ? (
    <AlertBox severity='error'>{loadingStudyIntakeError.message}</AlertBox>
  ) : (
    <ActivityIndicatorScreen backgroundColor='transparent' />
  );
};

interface RedirectConfig {
  redirectPath?: string;
  redirectFormTemplateId?: number;
  shouldLoadExistingFormData?: boolean;
}

export const RedirectPlugin: FormElementPlugin<any, RedirectConfig> = {
  name: 'Redirect',
  type: 'Redirect',
  element: Redirect as React.FC<FormElementProps<any, RedirectConfig>>
};
